import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { CfsApi, UnitApi } from '@/api';
import {
  AssignedUnitShiftFriendlyStatus,
  AssignedUnitShiftStatus,
} from '@/models';
import { ConfirmTypes, useToastStore } from '@/store';

import {
  ASSIGN_HQ_PREFIX,
  ASSIGN_STATUS,
  UPDATE_UNIT_SHIFT_STATUS_COMMAND_ID,
} from '../commands/UpdateUnitShiftStatusCommand';
import { StyledTextField } from '../style';
import { CommandDefinition, InputFieldDefinition } from '../types';
import { MultiSelectField } from './MultiSelectField';

interface CommandFormProps {
  command: CommandDefinition;
  onExecute: (values: Record<string, any>) => void;
  isLoading?: boolean;
  useHorizontalLayout?: boolean;
  initialValues?: Record<string, any>;
  onFocusCommandSelector?: () => void;
  onAreInputFieldsEmpty?: (isEmpty: boolean) => void;
}
//
export const CommandForm = ({
  command,
  onExecute,
  useHorizontalLayout = false,
  initialValues = {},
  onFocusCommandSelector,
  onAreInputFieldsEmpty,
}: CommandFormProps) => {
  const [values, setValues] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { updateToast } = useToastStore();
  const [attemptSubmitAfterChange, setAttemptSubmitAfterChange] =
    useState<boolean>(false);
  const inputRefs = useRef<Record<string, HTMLElement | null>>({});
  const [, setForceRenderKey] = useState(0); // Added for forcing re-renders
  const prevInitialValuesRef = useRef<Record<string, any>>({}); // Track previous initialValues

  // Check if all input fields are empty and notify parent component
  useEffect(() => {
    if (!onAreInputFieldsEmpty) return;

    const areAllFieldsEmpty = command.inputs.every((input) => {
      const value = values[input.id];
      return (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      );
    });

    onAreInputFieldsEmpty(areAllFieldsEmpty);
  }, [values, command.inputs, onAreInputFieldsEmpty]);

  const fetchUnitStatusData = useCallback(async () => {
    if (!command.inputs || !command.inputs[0]) {
      return;
    }
    command.inputs[0].loading = true;
    setForceRenderKey((prev) => prev + 1); // Trigger re-render

    try {
      const response = await UnitApi.getUnitStatusOverview();
      const responseData = response?.data;
      const editableUnits = Array.isArray(responseData)
        ? responseData.filter((unit) => !!unit.latestShiftId)
        : [];
      const options = editableUnits.map((unit) => ({
        label: unit.name,
        value: unit.latestShiftId || '',
        inputValue: unit.name,
        customStatusId: unit.customStatus?._id,
        description: '', // Add required description property
      }));
      command.inputs[0].options = options;
    } catch (err: any) {
      updateToast({
        open: true,
        message: 'Could not fetch unit statuses.',
        type: ConfirmTypes.WARNING,
      });
    } finally {
      if (command.inputs && command.inputs[0]) {
        // Check again before mutating
        command.inputs[0].loading = false;
        setForceRenderKey((prev) => prev + 1); // Trigger re-render
      }
    }
  }, [command, updateToast, setForceRenderKey]); // Updated dependencies

  const fetchAvailableStatuses = useCallback(async () => {
    if (command.inputs && command.inputs.length > 1 && command.inputs[1]) {
      command.inputs[1].loading = true;
      setForceRenderKey((prev) => prev + 1); // Trigger re-render
    } else {
      return;
    }
    try {
      const response = await UnitApi.getAvailableCustomStatuses();
      const statuses = response?.data ?? [];

      // Get selected units and their current status IDs
      const selectedUnitIds = values.unitId
        ? Array.isArray(values.unitId)
          ? values.unitId
          : [values.unitId]
        : [];

      // Extract the customStatusIds from the options of selected units
      const unitOptions =
        (command.inputs[0]?.options as Array<{
          label: string;
          value: string | number;
          customStatusId?: string | null;
        }>) || [];

      const currentStatusIds =
        selectedUnitIds.length > 0
          ? (unitOptions
              .filter((opt) => selectedUnitIds.includes(opt.value))
              .map((opt) => opt.customStatusId)
              .filter(Boolean) as string[])
          : [];

      // Filter out statuses that are already assigned to any selected unit
      const filteredStatuses =
        currentStatusIds.length > 0
          ? statuses.filter((status) => !currentStatusIds.includes(status._id))
          : statuses;

      if (command.inputs && command.inputs.length > 1 && command.inputs[1]) {
        // Update the statusId field options with filtered statuses
        command.inputs[1].options = filteredStatuses.map((status) => ({
          label: `${status.code}`,
          value: status._id,
          customData: status,
          description: status.description,
          searchableText: `${status.code} ${status.description || ''}`,
        }));
        command.inputs[1].loading = false;
        setForceRenderKey((prev) => prev + 1); // Trigger re-render
      }

      // We already set options above, no need to do it twice
    } catch (err: any) {
      updateToast({
        open: true,
        message: 'Could not load available statuses.',
        type: ConfirmTypes.WARNING,
      });
    } finally {
      if (command.inputs && command.inputs.length > 1 && command.inputs[1]) {
        command.inputs[1].loading = false;
        setForceRenderKey((prev) => prev + 1); // Trigger re-render
      }
    }
  }, [command, updateToast, setForceRenderKey, values]); // Added values to dependencies since we use it for filtering

  const fetchActiveCFSList = useCallback(async () => {
    if (!command.inputs?.[0]) return;
    command.inputs[0].loading = true;
    setForceRenderKey((prev) => prev + 1);
    try {
      const response = await CfsApi.activeList({
        sort: JSON.stringify({ createdAt: -1 }),
      });
      const cfsList = response?.data ?? [];
      command.inputs[0].options = cfsList
        .filter((cfs) => !!cfs._id)
        .map((cfs) => ({
          label: cfs.number,
          value: cfs._id as string,
          description: cfs.addressInfo?.address?.addressLabel ?? '',
          searchableText: `${cfs.number} ${
            cfs.addressInfo?.address?.addressLabel ?? ''
          }`,
        }));
    } catch {
      updateToast({
        open: true,
        message: 'Could not load CFS list.',
        type: ConfirmTypes.WARNING,
      });
    } finally {
      if (command.inputs?.[0]) {
        command.inputs[0].loading = false;
        setForceRenderKey((prev) => prev + 1);
      }
    }
  }, [command, updateToast]);

  const fetchUnitsForCFS = useCallback(
    async (cfsId: string, status: string) => {
      const unitInput = command.inputs?.[2];
      if (!unitInput) return;
      unitInput.loading = true;
      setForceRenderKey((prev) => prev + 1);
      try {
        const isAssign =
          status === ASSIGN_STATUS || status.startsWith(ASSIGN_HQ_PREFIX);
        const isHQAssign = status.startsWith(ASSIGN_HQ_PREFIX);

        if (isAssign) {
          const [cfsResponse, shiftsResponse] = await Promise.all([
            CfsApi.getOne(cfsId),
            UnitApi.getUnitShifts({
              filter: JSON.stringify({ status: 'ACTIVE' }),
              limit: 1000,
            }),
          ]);

          const alreadyAssignedShiftIds = new Set(
            (cfsResponse?.data?.assignedUnitShifts ?? [])
              .filter(
                (aus) =>
                  aus.status !== AssignedUnitShiftStatus.COMPLETED &&
                  aus.status !== AssignedUnitShiftStatus.CANCELED,
              )
              .map((aus) => aus.unitShift._id),
          );

          const activeShifts = shiftsResponse?.data?.results ?? [];
          unitInput.options = activeShifts
            .filter(
              (s) =>
                !!s._id &&
                !alreadyAssignedShiftIds.has(s._id as string) &&
                (!isHQAssign || s.unit.type.isDispatchToHQEnabled),
            )
            .map((s) => ({
              label: s.unit.name,
              value: s._id as string,
              description: '',
            }));
        } else {
          const cfsResponse = await CfsApi.getOne(cfsId);
          const assignedUnitShifts =
            cfsResponse?.data?.assignedUnitShifts ?? [];
          const lockedStatuses = new Set([
            AssignedUnitShiftStatus.QUEUED,
            AssignedUnitShiftStatus.COMPLETED,
            AssignedUnitShiftStatus.CANCELED,
          ]);
          unitInput.options = assignedUnitShifts
            .filter((aus) => !!aus._id && !lockedStatuses.has(aus.status))
            .map((aus) => ({
              label: aus.unitShift.unit.name,
              value: aus._id as string,
              description:
                AssignedUnitShiftFriendlyStatus[aus.status] ?? aus.status,
            }));
        }
      } catch {
        updateToast({
          open: true,
          message: 'Could not load units for this CFS.',
          type: ConfirmTypes.WARNING,
        });
      } finally {
        if (command.inputs?.[2]) {
          command.inputs[2].loading = false;
          setForceRenderKey((prev) => prev + 1);
        }
      }
    },
    [command, updateToast],
  );

  useEffect(() => {
    if (command.id === 'UPDATE-UNIT-STATUS') {
      fetchUnitStatusData();
      fetchAvailableStatuses();
    }
  }, [command, fetchUnitStatusData, fetchAvailableStatuses]);

  // Separate effect so fetchActiveCFSList is not pulled into the values-dependent
  // fetchAvailableStatuses dep chain, which would cause an infinite refetch loop.
  useEffect(() => {
    if (command.id === UPDATE_UNIT_SHIFT_STATUS_COMMAND_ID) {
      fetchActiveCFSList();
    }
  }, [command, fetchActiveCFSList]);
  // Initialize default values
  useEffect(() => {
    const defaultValues: Record<string, any> = {};
    command.inputs.forEach((input) => {
      if (input.defaultValue !== undefined) {
        defaultValues[input.id] = input.defaultValue;
      }
    });

    // Merge with initial values from history
    const mergedValues = { ...defaultValues, ...initialValues };
    setValues(mergedValues);
    setErrors({});
    // When command changes, clear old refs. They will be repopulated during render.
    inputRefs.current = {};
  }, [command, initialValues]);

  const handleChange = (id: string, value: any) => {
    // For the new Update Unit Shift Status command, clear units when CFS or status changes
    if (
      (id === 'cfsId' || id === 'status') &&
      command.id === UPDATE_UNIT_SHIFT_STATUS_COMMAND_ID
    ) {
      const newCfsId = id === 'cfsId' ? value : values.cfsId;
      const newStatus = id === 'status' ? value : values.status;
      // Clear stale unit options immediately so the dropdown is empty while fetching
      if (command.inputs?.[2]) {
        command.inputs[2].options = [];
      }
      setValues((prev) => ({ ...prev, [id]: value, unitShiftIds: [] }));
      setForceRenderKey((prev) => prev + 1);
      if (errors[id]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[id];
          return newErrors;
        });
      }
      if (newCfsId && newStatus) {
        fetchUnitsForCFS(newCfsId, newStatus);
      }
      return;
    }

    setValues((prev) => ({ ...prev, [id]: value }));

    // Clear error when field is edited
    if (errors[id]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }

    // If this is the unitId field and we're updating unit status, refetch available statuses
    if (id === 'unitId' && command.id === 'UPDATE-UNIT-STATUS') {
      fetchAvailableStatuses();
    }

    const currentIndex = command.inputs.findIndex((inp) => inp.id === id);
    const isLastInput = currentIndex === command.inputs.length - 1;

    // Special handling for multi-select fields - don't auto-advance on selection
    const isMultiSelect = command.inputs[currentIndex]?.multiSelect === true;

    if (isLastInput && !isMultiSelect) {
      setAttemptSubmitAfterChange(true);
    } else if (
      currentIndex !== -1 &&
      command.inputs[currentIndex + 1] &&
      !isMultiSelect
    ) {
      // Auto-focus to next field if we're setting a value (not clearing it) and not a multi-select
      const hasValue = value !== undefined && value !== null && value !== '';

      if (hasValue) {
        const nextInputId = command.inputs[currentIndex + 1].id;
        setTimeout(() => {
          const nextEl = inputRefs.current[nextInputId];
          if (nextEl && typeof (nextEl as any).focus === 'function') {
            (nextEl as any).focus();
          }
        }, 0);
      }
    }
  };

  const handleKeyDown = (id: string, event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const currentIndex = command.inputs.findIndex((inp) => inp.id === id);
      const isLastInput = currentIndex === command.inputs.length - 1;
      const isMultiSelect = command.inputs[currentIndex]?.multiSelect === true;

      if (isLastInput) {
        // If this is the last field, submit the form
        setAttemptSubmitAfterChange(true);
      } else if (
        currentIndex !== -1 &&
        command.inputs[currentIndex + 1] &&
        !isMultiSelect
      ) {
        // Move to the next field
        const nextInputId = command.inputs[currentIndex + 1].id;
        setTimeout(() => {
          const nextEl = inputRefs.current[nextInputId];
          if (nextEl && typeof (nextEl as any).focus === 'function') {
            (nextEl as any).focus();
          }
        }, 0);
      }
    } else if (event.key === 'Backspace') {
      const currentElement = inputRefs.current[id];
      const currentInput = command.inputs.find((inp) => inp.id === id);
      let isEmpty = false;

      // Check if the field is empty based on its type
      if (currentInput?.type === 'select') {
        // For autocomplete/select fields, check both the state value AND the actual input text
        const currentValue = values[id];
        const hasStateValue =
          currentValue !== undefined &&
          currentValue !== null &&
          currentValue !== '';

        // Check if there's text in the input field itself
        let hasInputText = false;
        if (currentElement) {
          const inputValue = (currentElement as any).value;
          hasInputText = inputValue && inputValue.trim() !== '';
        }

        // Consider empty only if both state value and input text are empty
        isEmpty = !hasStateValue && !hasInputText;
      } else if (currentElement) {
        // For text/number/date fields, check the actual input value
        const inputValue = (currentElement as any).value;
        isEmpty = !inputValue || inputValue.trim() === '';
      }

      if (isEmpty) {
        const currentIndex = command.inputs.findIndex((inp) => inp.id === id);

        if (currentIndex === 0) {
          // If this is the first field, focus the command selector
          event.preventDefault();
          if (onFocusCommandSelector) {
            onFocusCommandSelector();
          }
        } else if (currentIndex > 0) {
          event.preventDefault();
          const prevInputId = command.inputs[currentIndex - 1].id;
          setTimeout(() => {
            const prevEl = inputRefs.current[prevInputId];
            if (prevEl && typeof (prevEl as any).focus === 'function') {
              (prevEl as any).focus();
            }
          }, 0);
        }
      }
    }
  };

  const handleFormSubmit = useCallback(() => {
    // Validate fields
    const newErrors: Record<string, string> = {};

    for (const input of command.inputs) {
      const value = values[input.id];

      // Check required fields
      if (
        input.required &&
        (value === undefined || value === null || value === '')
      ) {
        newErrors[input.id] = 'This field is required';
        continue;
      }

      // Run custom validation if provided
      if (input.validation && value !== undefined && value !== null) {
        const error = input.validation(value);
        if (error) {
          newErrors[input.id] = error;
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onExecute(values);

    // Clear the form: first input (action) to null, others to defaultValue or cleared
    const resetValues: Record<string, any> = {};

    command.inputs.forEach((input) => {
      resetValues[input.id] = null; // Explicitly clear the first input
    });
    setValues(resetValues);
    setErrors({});
  }, [values, command.inputs, onExecute, setErrors, setValues]);

  useEffect(() => {
    if (attemptSubmitAfterChange) {
      handleFormSubmit();
      setAttemptSubmitAfterChange(false); // Reset the trigger
    }
  }, [attemptSubmitAfterChange, handleFormSubmit]);

  useEffect(() => {
    // Check if initialValues have changed (excluding empty objects)
    const hasInitialValues = Object.keys(initialValues).length > 0;
    const prevHasInitialValues =
      Object.keys(prevInitialValuesRef.current).length > 0;
    const initialValuesChanged =
      hasInitialValues &&
      JSON.stringify(initialValues) !==
        JSON.stringify(prevInitialValuesRef.current);

    // Update the ref with current initialValues
    prevInitialValuesRef.current = initialValues;

    if (command.inputs.length > 0) {
      if (initialValuesChanged) {
        // Focus the last input when new initialValues are present
        const lastInputId = command.inputs[command.inputs.length - 1].id;
        setTimeout(() => {
          const lastEl = inputRefs.current[lastInputId];
          if (lastEl && typeof (lastEl as any).focus === 'function') {
            (lastEl as any).focus();
          }
        }, 0);
      } else if (!hasInitialValues || !prevHasInitialValues) {
        // Focus the first input when the command changes or no initialValues
        const firstInputId = command.inputs[0].id;
        setTimeout(() => {
          const firstEl = inputRefs.current[firstInputId];
          if (firstEl && typeof (firstEl as any).focus === 'function') {
            (firstEl as any).focus();
          }
        }, 0);
      }
    }
  }, [command, initialValues]); // Re-run when command or initialValues change

  const renderField = (input: InputFieldDefinition, isLast = true) => {
    const { id, label, type, required, options, placeholder } = input;
    const value = values[id] !== undefined ? values[id] : '';
    const error = errors[id];
    // const isFirstInput = command.inputs.length > 0 && command.inputs[0].id === input.id; // Not needed for individual ref assignment

    const commonInputRefProps = {
      inputRef: (el: HTMLElement | null) => (inputRefs.current[id] = el),
    };

    switch (type) {
      case 'text':
        return (
          <TextField
            key={id}
            id={id}
            label={label}
            value={value}
            onChange={(e) => handleChange(id, e.target.value)}
            onKeyDown={(e) => handleKeyDown(id, e)}
            placeholder={placeholder}
            required={required}
            error={!!error}
            helperText={error}
            fullWidth
            {...commonInputRefProps}
          />
        );

      case 'number':
        return (
          <TextField
            key={id}
            id={id}
            label={label}
            type="number"
            value={value}
            onChange={(e) => handleChange(id, e.target.value)}
            onKeyDown={(e) => handleKeyDown(id, e)}
            placeholder={placeholder}
            required={required}
            error={!!error}
            helperText={error}
            fullWidth
            margin="normal"
            {...commonInputRefProps}
          />
        );

      case 'select': {
        // Check if this is a multi-select field
        const isMultiSelect = input.multiSelect === true;

        if (isMultiSelect) {
          // For multi-select, value should be an array
          // const valuesArray = Array.isArray(value)
          //   ? value
          //   : value
          //   ? [value]
          //   : [];

          // Find option objects that match the current values
          // const selectedOptions = valuesArray
          //   .map((val) => (options ?? []).find((opt) => opt.value === val))
          //   .filter(Boolean) as { label: string; value: string | number }[];

          return (
            <FormControl
              key={id}
              fullWidth
              error={!!error}
              sx={{ height: '100%', flexBasis: 0 }}
            >
              <MultiSelectField
                id={id}
                label={label}
                value={value}
                options={options ?? []}
                onChange={(newValues) => {
                  // Store array of IDs/values
                  handleChange(id, newValues);
                }}
                error={error}
                helperText={error}
                placeholder={placeholder}
                required={required}
                isLast={isLast}
                inputRef={(el: HTMLElement | null) =>
                  (inputRefs.current[id] = el)
                }
                onKeyDown={(e) => handleKeyDown(id, e)}
              />
            </FormControl>
          );
        }

        // Single select (original implementation)
        const selectedOption =
          (options ?? []).find((opt) => opt.value === value) || null;

        return (
          <FormControl
            key={id}
            fullWidth
            error={!!error}
            sx={{ height: '100%', flexBasis: 0 }}
          >
            <Autocomplete
              id={id}
              style={{
                flex: 1,
                height: '100%',
              }}
              sx={{
                '& .MuiFormControl-root': {
                  height: '100%',
                },
              }}
              value={selectedOption} // Pass the full option object or null
              loading={input.loading}
              disablePortal
              popupIcon={null} // Remove dropdown arrow icon
              autoSelect
              autoHighlight
              options={options ?? []}
              noOptionsText="Not found"
              getOptionLabel={(option) => option.label || ''} // Specify how to get the display label from an option object
              isOptionEqualToValue={(option, val) =>
                option && val && option.value === val.value
              } // Specify how to compare option objects
              // Custom filter function to search using searchableText              // Custom filter to search in searchableText or label
              filterOptions={(optionsList, state) => {
                const inputValue = state.inputValue.toLowerCase();
                return optionsList.filter((opt) => {
                  // Use searchableText for filtering when available, otherwise use label
                  const searchText = opt.searchableText || opt.label || '';
                  return searchText.toLowerCase().indexOf(inputValue) !== -1;
                });
              }}
              onChange={(_, newValue) => {
                handleChange(id, newValue ? newValue.value : null); // Store the ID (value property) or null
              }}
              ListboxProps={{ style: { maxHeight: 340 } }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography fontWeight={500}>{option.label}</Typography>
                    {option.description && (
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <StyledTextField
                  variant="filled"
                  {...params}
                  fullWidth
                  label={label}
                  onKeyDown={(e) => handleKeyDown(id, e)}
                  inputRef={(el: HTMLElement | null) =>
                    (inputRefs.current[id] = el)
                  } // Autocomplete's renderInput TextField needs its own ref assignment
                  // remove Left border
                  sx={{
                    '& .MuiFilledInput-root': {
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      borderLeft: 'none',
                      height: '100%',
                      paddingRight: '32px !important',
                      ...(!isLast && {
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        borderRight: 'none',
                      }),
                    },
                    '& .MuiAutocomplete-input': {
                      fontSize: '14px',
                    },
                    '& .MuiAutocomplete-endAdornment': {
                      right: '4px',
                    },
                  }}
                />
              )}
            />
          </FormControl>
        );
      }

      case 'date':
        return (
          <DatePicker
            key={id}
            label={label}
            value={value || null}
            onChange={(newValue) => handleChange(id, newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                margin: 'normal',
                error: !!error,
                helperText: error,
                required,
                onKeyDown: (e) => handleKeyDown(id, e),
                inputRef: (el: HTMLElement | null) =>
                  (inputRefs.current[id] = el), // DatePicker's TextField
              },
            }}
          />
        );

      case 'checkbox':
        return (
          <FormControlLabel
            key={id}
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) => handleChange(id, e.target.checked)}
                onKeyDown={(e) => handleKeyDown(id, e)}
                name={id}
                inputRef={(el: HTMLInputElement | null) =>
                  (inputRefs.current[id] = el)
                } // Checkbox ref
              />
            }
            label={label}
          />
        );

      default:
        return null;
    }
  };

  if (useHorizontalLayout) {
    return (
      <>
        {command.inputs.map((input, index) => (
          <Box key={input.id} flexGrow={1} flexBasis={0} minWidth={0}>
            {renderField(input, index === command.inputs.length - 1)}
          </Box>
        ))}
      </>
    );
  }
};
